import {KpiType} from '@hconnect/common/types'
import {ControlledInput, ControlledSelect, Button} from '@hm/fundamental'
import {yupResolver} from '@hookform/resolvers/yup'
import {mdiSendOutline, mdiClose} from '@mdi/js'
import {Stack} from '@mui/material'
import {useMemo} from 'react'
import {Controller, useForm} from 'react-hook-form'
import {useTranslation} from 'react-i18next'

import {PERFORMANCE_REVIEW_KPI_TYPES, FEATURE_NAMESPACE} from '../consts'
import {getPerformanceReviewCommentValidationSchema} from '../validators'

type FormFields = {
  comment: string
  kpiType: KpiType
}

type AddCommentFormProps = {
  onAddComment: (comment: string, kpiType: KpiType) => Promise<void>
  onCancel: () => void
  isMobile?: boolean
}

export const AddPerformanceReviewCommentForm = ({
  onAddComment,
  onCancel,
  isMobile = false
}: AddCommentFormProps) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const validationSchema = useMemo(() => getPerformanceReviewCommentValidationSchema({t}), [t])
  const {
    handleSubmit,
    control,
    reset,
    formState: {isDirty, isSubmitting, isValid}
  } = useForm<FormFields>({
    mode: 'onChange',
    shouldFocusError: false,
    defaultValues: {comment: '', kpiType: undefined},
    resolver: yupResolver(validationSchema)
  })

  const kpiOptions = PERFORMANCE_REVIEW_KPI_TYPES.map((kpi) => ({
    label: t(`kpis.label.${kpi}`),
    value: kpi
  }))

  const submitForm = handleSubmit(async (fields) => {
    await onAddComment(fields.comment, fields.kpiType)
    reset()
    onCancel()
  })

  const handleCancel = () => {
    reset()
    onCancel()
  }

  return (
    <Stack spacing={1}>
      <Controller
        control={control}
        name="comment"
        render={({field, fieldState}) => (
          <ControlledInput
            ref={field.ref}
            label={t('comments.label.enterComment')}
            name={field.name}
            value={field.value}
            onChange={field.onChange}
            onBlur={field.onBlur}
            isDisabled={field.disabled || isSubmitting}
            autoComplete="off"
            data-cy="comment-form-input"
            isTouched={fieldState.isTouched}
            error={fieldState.error?.message}
          />
        )}
      />
      <Controller
        control={control}
        name="kpiType"
        render={({field, fieldState}) => (
          <ControlledSelect
            className="w-full"
            label={t('comments.label.kpi')}
            name={field.name}
            values={kpiOptions}
            value={field.value}
            onChange={(value) => {
              field.onChange(value)
            }}
            onBlur={field.onBlur}
            isDisabled={field.disabled || isSubmitting}
            autoComplete="off"
            data-cy="comment-form-kpi-select"
            isTouched={fieldState.isTouched}
            error={fieldState.error?.message}
          />
        )}
      />
      <Stack
        direction={isMobile ? 'column-reverse' : 'row'}
        spacing={1}
        justifyContent="flex-end"
        width={{xs: '100%', sm: 'auto'}}
      >
        <Button
          variant="secondary"
          onClick={handleCancel}
          disabled={isSubmitting}
          icon={mdiClose}
          data-cy="comment-form-cancel-button"
          className={isMobile ? 'w-full' : undefined}
        >
          {t('comments.action.cancel')}
        </Button>
        <Button
          variant="primary"
          onClick={submitForm}
          disabled={!isValid || !isDirty || isSubmitting}
          icon={mdiSendOutline}
          iconPosition="right"
          data-cy="comment-form-send-button"
          className={isMobile ? 'w-full' : undefined}
        >
          {t('comments.action.send')}
        </Button>
      </Stack>
    </Stack>
  )
}
